/**
 * Collection Model / Product image toggle
 * Swaps primary/secondary product-card images within a product-list.
 * On cards using the image carousel (`feature-product-card-image-swipe`), jumps the carousel to
 * the product (index 0) or model (index 1) slide instead, since those cards render every image
 * as a swipeable slide rather than a separate product/model image pair.
 * Mode is kept in module memory across facet section re-renders; resets on full page load.
 */
let pageImageMode = "product";

const PRIMARY_CLASS = "product-card__image--primary";
const SECONDARY_CLASS = "product-card__image--secondary";

class CollectionImageToggle extends HTMLElement {
  connectedCallback() {
    this.listId = this.getAttribute("aria-controls");
    this.buttons = Array.from(this.querySelectorAll("[data-image-mode]"));

    this.onClick = (event) => {
      const button = event.target.closest("[data-image-mode]");
      if (!button || !this.contains(button)) return;
      this.setMode(button.getAttribute("data-image-mode"));
    };

    this.addEventListener("click", this.onClick);
    this.setMode(pageImageMode);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  get productList() {
    return this.listId ? document.getElementById(this.listId) : null;
  }

  setMode(mode) {
    if (mode !== "product" && mode !== "model") return;

    pageImageMode = mode;

    this.buttons.forEach((button) => {
      const isActive = button.getAttribute("data-image-mode") === mode;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    const list = this.productList;
    if (!list) return;

    list.setAttribute("data-collection-image-mode", mode);
    list.querySelectorAll("product-card").forEach((card) => this.applyModeToCard(card, mode));
  }

  applyModeToCard(card, mode) {
    const carousel = card.querySelector(".product-card__media-carousel scroll-carousel");
    if (carousel) {
      this.applyModeToCarousel(carousel, mode);
      return;
    }

    const productImage = card.querySelector(".product-card__image--role-product");
    const modelImage = card.querySelector(".product-card__image--role-model");
    if (!productImage || !modelImage) return;

    const primary = mode === "model" ? modelImage : productImage;
    const secondary = mode === "model" ? productImage : modelImage;

    primary.classList.add(PRIMARY_CLASS);
    primary.classList.remove(SECONDARY_CLASS);
    primary.removeAttribute("aria-hidden");

    secondary.classList.add(SECONDARY_CLASS);
    secondary.classList.remove(PRIMARY_CLASS);
    secondary.setAttribute("aria-hidden", "true");

    // Keep DOM order: primary first for layout/accessibility consistency
    const media = primary.parentElement;
    if (media && primary !== media.firstElementChild) {
      media.insertBefore(primary, secondary);
    }
  }

  applyModeToCarousel(carousel, mode) {
    if (typeof carousel.select !== "function") return;

    // Slide order mirrors the classic pairing: product.media[0] is "product", [1] is "model".
    const targetIndex = mode === "model" ? 1 : 0;
    if (targetIndex >= carousel.cells.length) return;

    carousel.select(targetIndex, { instant: true });
  }
}

if (!window.customElements.get("collection-image-toggle")) {
  window.customElements.define("collection-image-toggle", CollectionImageToggle);
}
